import { useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  useAddKnowledgeBaseDocument,
  useKnowledgeBaseDocuments,
  useKnowledgeDocuments,
} from '../../hooks/useKnowledge';
import { KnowledgeStatusBadge } from './KnowledgeStatusBadge';

/**
 * "Add documents" — a picker over the whole account library (task 574).
 *
 * Nothing is uploaded here: a knowledge base groups documents that already
 * exist, so this only creates membership rows. Documents already in the base are
 * filtered out, and a document's ingestion status is shown because adding one
 * that is still `indexing` is allowed but will not be searchable until it is
 * `ready`.
 */
interface KnowledgeBaseDocumentPickerProps {
  knowledgeBaseId: string;
  isOpen: boolean;
  onClose: () => void;
}

/** One page is enough to pick from; the search box narrows server-side. */
const LIBRARY_PAGE_SIZE = 100;

export function KnowledgeBaseDocumentPicker({
  knowledgeBaseId,
  isOpen,
  onClose,
}: KnowledgeBaseDocumentPickerProps) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error } = useKnowledgeDocuments(1, LIBRARY_PAGE_SIZE, {
    search: search || undefined,
  });
  const { data: memberships } = useKnowledgeBaseDocuments(knowledgeBaseId);
  const addDocument = useAddKnowledgeBaseDocument();

  const allDocuments = data?.data ?? [];
  const memberIds = useMemo(
    () => new Set((memberships ?? []).map((membership) => membership.documentId)),
    [memberships]
  );
  const available = useMemo(
    () => allDocuments.filter((document) => !memberIds.has(document.id)),
    [allDocuments, memberIds]
  );

  const handleClose = () => {
    if (addDocument.isPending) return;
    setSearch('');
    setSelectedId(null);
    onClose();
  };

  const handleAdd = async () => {
    if (!selectedId) return;
    try {
      await addDocument.mutateAsync({ knowledgeBaseId, documentId: selectedId });
      setSearch('');
      setSelectedId(null);
      onClose();
    } catch (addError) {
      console.error('Failed to add the document to the knowledge base:', addError);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="knowledge-base-document-picker-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3
            id="knowledge-base-document-picker-title"
            className="text-lg font-medium text-gray-900 dark:text-white"
          >
            Add documents
          </h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={addDocument.isPending}
            aria-label="Close dialog"
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4">
          <div className="relative mb-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search the document library..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading documents...</div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 dark:text-red-400">
                Error loading documents. Please try again.
              </p>
            </div>
          ) : available.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {search
                  ? `No documents found matching "${search}"`
                  : allDocuments.length === 0
                    ? 'Your account has no documents yet.'
                    : 'Every document in your library is already in this knowledge base.'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {available.map((document) => (
                <button
                  key={document.id}
                  type="button"
                  onClick={() => setSelectedId(document.id)}
                  data-testid={`picker-document-${document.id}`}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    selectedId === document.id
                      ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <span className="font-medium text-gray-900 dark:text-white truncate">
                      {document.name}
                    </span>
                    <KnowledgeStatusBadge document={document} showFailureReason={false} />
                  </div>
                  {document.description && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                      {document.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={addDocument.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!selectedId || addDocument.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 border border-transparent rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addDocument.isPending ? 'Adding...' : 'Add to knowledge base'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KnowledgeBaseDocumentPicker;
